import { api, apiUrl, resolveTenantSlug, tokens } from './api';

export const NEWS_KINDS = ['Achievement', 'Result', 'Sports', 'Event', 'Notice', 'Gallery', 'Placement', 'Newsletter'] as const;
export const NEWS_AUDIENCES = ['AllFamilies', 'Everyone', 'AllStaff', 'ClassFamilies', 'RouteFamilies'] as const;

export interface NewsMedia { id: string; kind: string; url: string; caption: string | null; sortOrder: number; }

export interface NewsPost {
  id: string; kind: string; title: string; body: string; highlight: string | null;
  studentId: string | null; studentName: string | null;
  audience: string; audienceIds: string[];
  notifyOnPublish: boolean; sendEmail: boolean; sendSms: boolean;
  expiresOn: string | null; pinned: boolean;
  isPublic: boolean;
  status: string; recipientCount: number;
  createdAt: string; publishedAt: string | null;
  media: NewsMedia[];
}

export interface FeedCard {
  id: string; kind: string; title: string; body: string; highlight: string | null;
  studentName: string | null; pinned: boolean; publishedAt: string; media: NewsMedia[];
}

export interface PagedNews { items: NewsPost[]; total: number; skip: number; take: number; }
export interface AudiencePreview { count: number; sampleNames: string[]; }

export const newsApi = {
  list: (opts?: { kind?: string; status?: string; skip?: number; take?: number }) => {
    const q = new URLSearchParams();
    if (opts?.kind) q.set('kind', opts.kind);
    if (opts?.status) q.set('status', opts.status);
    if (opts?.skip != null) q.set('skip', String(opts.skip));
    if (opts?.take != null) q.set('take', String(opts.take));
    const qs = q.toString();
    return api.get<PagedNews>(`/api/news/${qs ? `?${qs}` : ''}`);
  },
  get: (id: string) => api.get<NewsPost>(`/api/news/${id}`),
  create: (b: Partial<NewsPost> & { institutionId: string }) => api.post<NewsPost>('/api/news/', b),
  update: (id: string, b: Partial<NewsPost>) => api.put<NewsPost>(`/api/news/${id}`, b),
  publish: (id: string) => api.post<NewsPost>(`/api/news/${id}/publish`, {}),
  archive: (id: string) => api.post<NewsPost>(`/api/news/${id}/archive`, {}),
  setPublic: (id: string, show: boolean) => api.post<NewsPost>(`/api/news/${id}/public/${show ? 'on' : 'off'}`, {}),
  preview: (institutionId: string, audience: string, audienceIds?: string[]) =>
    api.post<AudiencePreview>('/api/news/preview', { institutionId, audience, audienceIds: audienceIds ?? [] }),
  removeMedia: (id: string, mediaId: string) => api.del<NewsPost>(`/api/news/${id}/media/${mediaId}`),

  feed: (take = 30) => api.get<FeedCard[]>(`/api/news/feed?take=${take}`),
  forStudent: (studentId: string, take = 10) => api.get<FeedCard[]>(`/api/news/student/${studentId}?take=${take}`),

  // multipart media upload
  uploadMedia: async (id: string, file: File, caption?: string): Promise<NewsPost> => {
    const fd = new FormData();
    fd.append('file', file);
    if (caption) fd.append('caption', caption);
    const slug = resolveTenantSlug();
    const res = await fetch(apiUrl(`/api/news/${id}/media`), {
      method: 'POST',
      headers: {
        ...(slug ? { 'X-Tenant': slug } : {}),
        ...(tokens.access() ? { Authorization: `Bearer ${tokens.access()}` } : {}),
      },
      body: fd,
    });
    if (!res.ok) throw new Error('Upload failed');
    return (await res.json()) as NewsPost;
  },
};

// media url through the app origin (the endpoint is auth-gated; <img> carries the session cookie/
// bearer via the same-origin fetch the browser makes — for cross-origin we resolve to apiUrl).
export const mediaSrc = (url: string) => apiUrl(url);

export function kindEmoji(kind: string): string {
  switch (kind) {
    case 'Achievement': return '🏆';
    case 'Result': return '🎯';
    case 'Sports': return '⚽';
    case 'Event': return '🎉';
    case 'Gallery': return '📷';
    case 'Placement': return '💼';
    case 'Newsletter': return '📰';
    default: return '📢';
  }
}
